import axios from 'axios';
import { authService } from './authService';
import config from '../config/environment';

// Payment types
export interface CreatePaymentOrderRequest {
  serviceRequestUuid?: string; // For single service bookings
  groupBookingId?: string; // For cart/group bookings
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  totalAmount?: number; // Optional total amount including GST
  paymentMode?: 'PREPAID' | 'POSTPAID';
  gstBillingInfo?: {
    billingName: string;
    gstNumber: string;
    billingAddressLine1: string;
    billingAddressLine2: string;
    city: string;
    state: string;
    pincode: string;
    phone: string;
    email: string;
    isDefault: boolean;
  };
}

export interface PaymentOrderResponse {
  orderId: string;
  razorpayOrderId: string;
  amount: number;
  currency: string;
  status: string;
  razorpayKeyId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

export interface VerifyPaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
  serviceRequestUuid: string;
  paymentStatus: string;
  paymentId?: string;
  amount?: number;
  paymentMethod?: string;
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

// Create axios instance for payment API calls
const paymentApi = axios.create({
  baseURL: config.authServerUrl, // Using same base URL as auth service
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include access token
paymentApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
paymentApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshAccessToken();
        const token = localStorage.getItem('access_token');
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return paymentApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        authService.logoutLocal();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Load Razorpay script dynamically
const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if Razorpay is already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

export const paymentService = {
  /**
   * Create payment order for a service request
   */
  createPaymentOrder: async (request: CreatePaymentOrderRequest): Promise<PaymentOrderResponse> => {
    try {
      console.log('💳 Creating payment order with GST-inclusive amount:', request.totalAmount);
      
      const response = await paymentApi.post('/api/payments/create-order', request);
      
      console.log('✅ Payment order created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating payment order:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to create payment order';
          throw new Error(`Failed to create payment order: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to create payment order: ${(error as Error).message}`);
    }
  },

  /**
   * Verify payment after successful Razorpay transaction
   */
  verifyPayment: async (request: VerifyPaymentRequest): Promise<VerifyPaymentResponse> => {
    try {
      console.log('🔐 Verifying payment:', request);
      
      const response = await paymentApi.post('/api/payments/verify', request);
      
      console.log('✅ Payment verified successfully:', response.data);
      
      // Transform backend response to match frontend expectations
      const backendData = response.data;
      const transformedResponse: VerifyPaymentResponse = {
        success: backendData.status === 'PAID',
        message: backendData.status === 'PAID' ? 'Payment verified successfully' : 'Payment verification failed',
        serviceRequestUuid: backendData.serviceRequestUuid,
        paymentStatus: backendData.status,
        paymentId: backendData.paymentId,
        amount: backendData.amount,
        paymentMethod: backendData.paymentMethod
      };
      
      console.log('🔄 Transformed response for frontend:', transformedResponse);
      return transformedResponse;
    } catch (error) {
      console.error('❌ Error verifying payment:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to verify payment';
          throw new Error(`Failed to verify payment: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to verify payment: ${(error as Error).message}`);
    }
  },

  /**
   * Process payment using Razorpay
   */
  processPayment: async (
    paymentOrder: PaymentOrderResponse,
    customerInfo: {
      name: string;
      email: string;
      phone: string;
    },
    options?: {
      description?: string;
      image?: string;
    }
  ): Promise<RazorpayResponse> => {
    try {
      console.log('🚀 Processing payment with Razorpay (amount includes GST):', paymentOrder.amount);
      
      // Load Razorpay script
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        throw new Error('Failed to load Razorpay script');
      }

      return new Promise((resolve, reject) => {
        const razorpayOptions = {
          key: paymentOrder.razorpayKeyId,
          amount: paymentOrder.amount * 100, // Use the updated amount (includes GST)
          currency: paymentOrder.currency,
          order_id: paymentOrder.razorpayOrderId,
          name: 'Spiritual Services',
          description: options?.description || 'Payment for spiritual service',
          image: options?.image || '/images/logo.png',
          prefill: {
            name: customerInfo.name,
            email: customerInfo.email,
            contact: customerInfo.phone,
          },
          notes: {
            description: options?.description || 'Payment for spiritual service',
            service_type: 'spiritual_service'
          },
          theme: {
            color: '#ff6b35',
          },
          modal: {
            ondismiss: () => {
              console.log('💬 Payment modal dismissed by user');
              reject(new Error('Payment cancelled by user'));
            },
            confirm_close: true,
            escape: true,
            animation: true,
            backdropclose: false
          },
          handler: (response: RazorpayResponse) => {
            console.log('✅ Payment completed:', response);
            resolve(response);
          },
          retry: {
            enabled: true,
            max_count: 4
          },
        };

        const rzp = new window.Razorpay(razorpayOptions);
        
        rzp.on('payment.failed', (response: any) => {
          console.error('❌ Payment failed:', response.error);
          const errorMessage = response.error?.description || 'Payment failed';
          
          // Don't reject immediately for certain errors - let user retry with different card/method
          if (response.error?.code === 'BAD_REQUEST_ERROR' || 
              response.error?.code === 'GATEWAY_ERROR' ||
              response.error?.reason === 'payment_failed') {
            console.log('🔄 Payment failed, but user can retry with different method');
            // Modal stays open for user to retry with different payment method
            return; // Don't reject the promise yet
          } else {
            // Other errors that should close modal
            console.error('❌ Fatal payment error, closing modal:', response.error);
            reject(new Error(`Payment failed: ${errorMessage}`));
          }
        });

        rzp.open();
      });
    } catch (error) {
      console.error('❌ Error processing payment:', error);
      throw error;
    }
  },

  /**
   * Complete payment flow: create order, process payment, verify payment
   */
  completePaymentFlow: async (
    identifier: string, // Can be serviceRequestUuid or groupBookingId
    customerInfo: {
      name: string;
      email: string;
      phone: string;
    },
    options?: {
      description?: string;
      image?: string;
      totalAmount?: number;
      isGroupBooking?: boolean;
    }
  ): Promise<VerifyPaymentResponse> => {
    try {
      console.log('🎯 Starting complete payment flow for:', identifier);
      
      // Step 1: Create payment order with total amount including GST
      const paymentOrderRequest: CreatePaymentOrderRequest = {
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        totalAmount: options?.totalAmount // Pass total amount to backend
      };
      
      // Set either serviceRequestUuid or groupBookingId based on the flow
      if (options?.isGroupBooking) {
        paymentOrderRequest.groupBookingId = identifier;
      } else {
        paymentOrderRequest.serviceRequestUuid = identifier;
      }

      const paymentOrder = await paymentService.createPaymentOrder(paymentOrderRequest);

      // Step 2: Process payment with Razorpay (order should already have correct amount)
      const razorpayResponse = await paymentService.processPayment(
        paymentOrder, // Use order as-is since it should have correct amount
        customerInfo,
        options
      );

      // Step 3: Verify payment
      const verificationResult = await paymentService.verifyPayment({
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
      });

      console.log('🎉 Payment flow completed successfully:', verificationResult);
      return verificationResult;
    } catch (error) {
      console.error('❌ Payment flow failed:', error);
      throw error;
    }
  },
};

// Declare Razorpay interface for TypeScript
declare global {
  interface Window {
    Razorpay: any;
  }
}

export default paymentService;